import React, { useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";

import DashboardCard from "./DashboardCard";
import ShareReferralLink, { RatedOnTrustpilot } from "./ShareReferralLink";
import { trackEvent } from "../utils/analyticsUtils";
import {
  BUYER_REWARD_CONDITION,
  CLIENT_REFERRAL_TERMS_URL,
  REWARD_CONDITION,
  amountForCopy,
  referralEmailSubject,
  referralShareText,
  rewardForCopy,
  voucherForCopy,
} from "../utils/referralUtils";

// The copy for each ask. Amounts come from the referral code, never from here,
// and the condition on the voucher is worded once in referralUtils.
const copyFor = (prompt, discount, rewardAmount) => {
  const voucher = voucherForCopy(rewardAmount);
  if (prompt === "refer_buyer") {
    const intro = `Both sides on one platform means fewer delays and everyone sees the same progress. Your buyer gets ${discount} off`;
    return {
      heading: "When you agree a sale, point your buyer to Juno",
      body: rewardForCopy(rewardAmount) ? `${intro}, and you get ${voucher} ${BUYER_REWARD_CONDITION}.` : `${intro}.`,
    };
  }
  return {
    heading: "Know someone buying or selling?",
    body: `They’ll get ${discount} off their conveyancing with your link, and we’ll send you ${voucher} ${REWARD_CONDITION}.`,
  };
};

function ReferralCard({ id, referralUrl, rewardAmount, discountAmount, prompt, transactionType }) {
  const discount = amountForCopy(discountAmount);
  const reward = rewardForCopy(rewardAmount);
  const { heading, body } = copyFor(prompt, discount, rewardAmount);

  useEffect(() => {
    trackEvent("referral_banner_view", { variant: prompt, reward });
  }, [prompt, reward]);

  return (
    <DashboardCard id={id} title={heading}>
      <p>{body}</p>
      <ShareReferralLink
        referralUrl={referralUrl}
        shareText={referralShareText(discount, transactionType)}
        emailSubject={referralEmailSubject(discount)}
        variant={prompt}
        utmSource="client_dashboard"
      />
      <SmallPrint>
        <RatedOnTrustpilot /> Discount and voucher only available for new transactions.{" "}
        <a href={CLIENT_REFERRAL_TERMS_URL} target="_blank" rel="noopener noreferrer">
          Terms apply
        </a>
      </SmallPrint>
    </DashboardCard>
  );
}

ReferralCard.propTypes = {
  id: PropTypes.string,
  // The link the backend built for the client's code
  referralUrl: PropTypes.string.isRequired,
  // "£200.00" strings from the dashboard payload; shown without the pence
  rewardAmount: PropTypes.string,
  discountAmount: PropTypes.string.isRequired,
  // The case's client_referral_prompt; CaseDetails passes "refer_friend" when the case has none
  prompt: PropTypes.oneOf(["refer_buyer", "refer_friend"]).isRequired,
  // "purchase", "sale" or "buyingandselling" (see describeTransactionType),
  // for the pre-written share message
  transactionType: PropTypes.string,
};

export default ReferralCard;

const SmallPrint = styled.p`
  font-size: var(--juno-font-size-body-sm);
  color: var(--juno-color-text-secondary);
  margin: 0;

  a {
    color: inherit;
  }
`;
